/**
 * ENTITLEMENT-LIFECYCLE-1 Phase 1 — the shapes the entitlements screen reads.
 *
 * Split out of EntitlementsPage.tsx under docs/FILE_SIZE_POLICY.md (the page
 * crossed the 350-line warning band). Types and labels only: no state, no calls.
 * The server remains the authority on every value here — `ROLE_OPTIONS` mirrors
 * the service's vocabulary so the operator is offered what will be accepted, and
 * a stale copy costs a 422, never a silent wrong grant.
 */

import { formatMediumDate } from '@/utils/date'

export interface ProductCell {
  entitled: boolean
  ever_granted: boolean
  grant_id?: string | null
  role?: string | null
  scope?: string | null
  granted_by?: string | null
  granted_at?: string | null
  revoked_by?: string | null
  revoked_at?: string | null
  note?: string | null
}

export interface FirmRow {
  account_id: string
  products: Record<string, ProductCell>
}

export interface EntitlementsResponse {
  firms: FirmRow[]
  count: number
  products: string[]
  suggested_reasons: string[]
}

export interface PendingAction {
  action: 'grant' | 'revoke'
  accountId: string
  product: string
}

export const PRODUCT_LABELS: Record<string, string> = {
  qoe: 'QoE',
  otcid: 'OTCID',
  valuation: 'Valuation',
  attest: 'Verification',
}

/** Mirrors `GRANT_ROLES` in the service. The server rejects anything else. */
export const ROLE_OPTIONS = ['cpa', 'analyst', 'firm', 'valuation_signer', 'valuation_owner', 'tester']

/** The role a grant carries when the operator does not choose one. */
export const DEFAULT_ROLE = 'cpa'

export function productLabel(product: string): string {
  return PRODUCT_LABELS[product] ?? product
}

export function fmtDate(iso?: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return String(iso)
  return formatMediumDate(iso)
}
